import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import * as Sentry from '@sentry/angular';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { scrubSensitive, stripQuery } from './app/util/pii-scrub.util';

Sentry.init({
  dsn: environment.sentryDsn,
  // Toggled per-environment (off in local environment.ts).
  enabled: environment.enableSentry,
  environment: environment.name,
  release: environment.version,
  // Never let Sentry auto-attach IP address / referrer / other default PII.
  sendDefaultPii: false,
  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  // Only attach tracing headers to our own APIs so we don't leak the trace to third parties.
  tracePropagationTargets: ['localhost', environment.apiURL],
  // Keep sampling modest to limit performance/load impact.
  tracesSampleRate: 0.2,
  // Strip PII and auth material from every event before it's sent.
  beforeSend(event) {
    if (event.request) {
      if (event.request.url) {
        event.request.url = stripQuery(event.request.url);
      }
      delete (event.request as any).query_string;
      delete event.request.cookies;
      if (event.request.data) {
        event.request.data = scrubSensitive(event.request.data);
      }
      const headers = event.request.headers as Record<string, string> | undefined;
      if (headers) {
        for (const h of Object.keys(headers)) {
          if (/^(authorization|cookie|x-.*token|x-api-key)$/i.test(h)) {
            delete headers[h];
          }
        }
      }
    }
    if (event.extra) {
      event.extra = scrubSensitive(event.extra);
    }
    if (event.contexts) {
      event.contexts = scrubSensitive(event.contexts);
    }
    return event;
  },
  // Breadcrumbs auto-capture console logs + every fetch/xhr URL + navigation.
  // Strip query strings and scrub structured data before they're attached.
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.data) {
      if (typeof breadcrumb.data['url'] === 'string') {
        breadcrumb.data['url'] = stripQuery(breadcrumb.data['url']);
      }
      breadcrumb.data = scrubSensitive(breadcrumb.data);
    }
    return breadcrumb;
  },
});

// Mixpanel product analytics (NEWTON-483) is initialized lazily inside
// AnalyticsService (dynamic import) so the SDK stays out of the initial bundle.

if (environment.production) {
  enableProdMode();
}

platformBrowserDynamic().bootstrapModule(AppModule)
  .catch(err => console.error(err));
